import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import {
  IInviteExperts,
  IInviteExternalExpert
} from '../components/Submission/BoardMemberReviewsTab/InviteExperts/commonTypes';
import { useState } from 'react';
import { useToast } from '@morressier/alembic';
import { validateAdjudicatorEligibility } from '../api/queries';
import { ISubmissionPreference } from '@publishing-engine/domain/models';
import { ADJUDICATOR_INVITE_ERROR_MESSAGES } from '@publishing-engine/domain/utils';

type IExpert = IInviteExperts & IInviteExternalExpert;
type IWarning = Record<number, { title: string; message: string }>;

export const useAdjudicatorValidation = (setError: any) => {
  const [warnings, setWarnings] = useState<IWarning>({});
  const toast = useToast();
  const {
    submissionId,
    fieldsResponse: {
      fields: { OpposedReviewers }
    }
  } = useSubmissionData();

  const opposedReviewerEmails = (
    (OpposedReviewers?.value as ISubmissionPreference['opposedReviewers'])
      ?.value || []
  ).map(opposedReviewer => opposedReviewer.email);

  const validateAdjudicators = async (experts: IExpert[]) => {
    const emails = experts.map(
      expert => expert.email || expert.externalExpertEmail
    );

    let hasErrors = false;

    emails.forEach((email, index) => {
      if (!email) {
        hasErrors = true;
        setError(`experts.${index}.email`, {
          type: 'required',
          message: 'Select an Expert to invite.'
        });
      }
    });

    // validate eligibility
    if (!hasErrors) {
      try {
        const eligibility = await validateAdjudicatorEligibility(
          submissionId,
          emails
        );

        eligibility.forEach((eligible, index) => {
          if (!eligible.eligible) {
            hasErrors = true;
            setError(`experts.${index}.email`, {
              type: 'eligibilityError',
              message: eligible.message
            });
          }
        });
      } catch (error) {
        toast({
          variant: 'error',
          title: 'Error validating adjudicator eligibility',
          description: (error as Error).message,
          isClosable: true
        });

        return false;
      }
    }
    return !hasErrors;
  };

  const validateOpposedReviewer = (experts: IExpert[]) => {
    const warns: IWarning = {};

    experts.forEach((expert, index) => {
      const exportEmail = expert.email || expert.externalExpertEmail;
      if (
        opposedReviewerEmails.some(
          email => email === exportEmail.toLowerCase().trim()
        )
      ) {
        warns[index] = {
          title: 'Opposed Reviewer',
          message: ADJUDICATOR_INVITE_ERROR_MESSAGES.OPPOSED_REVIEWER
        };
      }
    });

    setWarnings(warns);
  };

  return {
    validateOpposedReviewer,
    validateAdjudicators,
    warnings
  };
};
